import { useRef, useEffect, useState } from "react";
import { useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";

// Predefined facial expressions
const EXPRESSIONS = {
  neutral: {},
  smile: {
    mouthSmileLeft: 1,
    mouthSmileRight: 1,
    cheekSquintLeft: 0.4,
    cheekSquintRight: 0.4,
    eyeSquintLeft: 0.3,
    eyeSquintRight: 0.3,
  },
  bigSmile: {
    mouthSmileLeft: 1,
    mouthSmileRight: 1,
    cheekSquintLeft: 1,
    cheekSquintRight: 1,
    eyeSquintLeft: 0.6,
    eyeSquintRight: 0.6,
    mouthOpen: 0.3,
    browOuterUpLeft: 0.3,
    browOuterUpRight: 0.3,
  },
  surprise: {
    eyeWideLeft: 1,
    eyeWideRight: 1,
    browOuterUpLeft: 1,
    browOuterUpRight: 1,
    browInnerUp: 1,
    jawOpen: 1,
  },
  shock: {
    eyeWideLeft: 1,
    eyeWideRight: 1,
    browOuterUpLeft: 1,
    browOuterUpRight: 1,
    browInnerUp: 1,
    jawOpen: 1,
    mouthOpen: 1,
    mouthFunnel: 0.5,
  },
  angry: {
    browDownLeft: 1,
    browDownRight: 1,
    eyeSquintLeft: 0.5,
    eyeSquintRight: 0.5,
    noseSneerLeft: 0.5,
    noseSneerRight: 0.5,
    mouthFrownLeft: 0.7,
    mouthFrownRight: 0.7,
  },
  fury: {
    browDownLeft: 1,
    browDownRight: 1,
    eyeSquintLeft: 0.8,
    eyeSquintRight: 0.8,
    noseSneerLeft: 1,
    noseSneerRight: 1,
    mouthFrownLeft: 1,
    mouthFrownRight: 1,
    jawOpen: 0.4,
    mouthStretchLeft: 0.5,
    mouthStretchRight: 0.5,
  },
  sad: {
    browInnerUp: 0.5,
    mouthFrownLeft: 0.7,
    mouthFrownRight: 0.7,
    mouthLowerDownLeft: 0.4,
    mouthLowerDownRight: 0.4,
  },
  crying: {
    browInnerUp: 1,
    browDownLeft: 0.3,
    browDownRight: 0.3,
    mouthFrownLeft: 1,
    mouthFrownRight: 1,
    mouthLowerDownLeft: 0.7,
    mouthLowerDownRight: 0.7,
    eyeSquintLeft: 0.5,
    eyeSquintRight: 0.5,
  },
  kiss: {
    mouthPucker: 1,
    mouthRollUpper: 0.3,
    mouthRollLower: 0.3,
    eyeSquintLeft: 0.3,
    eyeSquintRight: 0.3,
  },
  contempt: {
    mouthStretchLeft: 0.5,
    eyeSquintLeft: 0.5,
    noseSneerLeft: 0.5,
    mouthRollUpper: 0.3,
    browDownRight: 0.3,
  },
  doubt: {
    browInnerUp: 0.5,
    browOuterUpLeft: 0.3,
    eyeSquintRight: 0.5,
    mouthStretchLeft: 0.3,
    jawLeft: 0.2,
  },
  thinking: {
    browDownLeft: 0.3,
    browDownRight: 0.3,
    eyeLookUpLeft: 0.7,
    eyeLookUpRight: 0.7,
    mouthPressLeft: 0.5,
    mouthPressRight: 0.5,
  },
  smirk: {
    mouthSmileLeft: 0.7,
    cheekSquintLeft: 0.3,
    eyeSquintLeft: 0.2,
    browOuterUpLeft: 0.2,
  },
  blink: {
    eyeBlinkLeft: 1,
    eyeBlinkRight: 1,
  },
  lookAround: {
    eyeLookOutLeft: 0.7,
    eyeLookOutRight: 0.7,
  },
};

export function Sentiment({
  position = [0, -1.17, 0],
  scale = 0.7,
  rotation = [0, 0, 0],
}) {
  const group = useRef();
  const morphMeshesRef = useRef([]);
  const { nodes } = useGLTF("/models/sentiment.glb");
  const [morphTargetNames, setMorphTargetNames] = useState([]);

  // Animation state
  const state = useRef({
    currentExpression: "neutral",
    nextExpression: "smile",
    transitionProgress: 0,
    lastBlinkTime: 0,
    blinkProgress: 0,
    isBlinking: false,
    expressionDuration: 3, // How long to hold each expression
    transitionDuration: 1.5, // How long to transition between expressions
    currentValues: {}, // Current morph target values
    targetValues: {}, // Target morph target values
  });

  useEffect(() => {
    // Collect all meshes that contain morph targets and build a unified set of target names
    morphMeshesRef.current = [];
    const nameSet = new Set();

    group.current?.traverse((object) => {
      if (object.isMesh && object.morphTargetDictionary) {
        morphMeshesRef.current.push(object);
        Object.keys(object.morphTargetDictionary).forEach((key) =>
          nameSet.add(key)
        );
      }
    });

    const names = Array.from(nameSet);
    setMorphTargetNames(names);

    // Initialize current and target values for all discovered morph target names
    state.current.currentValues = names.reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {});
    state.current.targetValues = { ...state.current.currentValues };
  }, []);

  // Smooth interpolation function with spring characteristics
  const springInterpolation = (start, end, progress) => {
    const p = Math.min(1, Math.max(0, progress));
    return start + ((end - start) * (1 - Math.cos(p * Math.PI))) / 2;
  };

  // Set morph target with smooth interpolation
  const setMorphTargetValue = (
    targetName,
    currentValue,
    targetValue,
    progress
  ) => {
    if (!morphMeshesRef.current.length) return;
    const value = springInterpolation(currentValue, targetValue, progress);
    // Apply to all meshes that have this morph target
    morphMeshesRef.current.forEach((mesh) => {
      const dict = mesh.morphTargetDictionary;
      if (
        dict &&
        dict[targetName] !== undefined &&
        mesh.morphTargetInfluences
      ) {
        mesh.morphTargetInfluences[dict[targetName]] = value;
      }
    });
    state.current.currentValues[targetName] = value;
  };

  // Update target values for next expression
  const updateTargetValues = (expressionName) => {
    const expression = EXPRESSIONS[expressionName];
    const newTargetValues = { ...state.current.currentValues };

    // Reset non-used targets to 0
    Object.keys(newTargetValues).forEach((key) => {
      newTargetValues[key] = expression[key] || 0;
    });

    state.current.targetValues = newTargetValues;
  };

  // Pick random next expression
  const pickNextExpression = () => {
    const expressions = Object.keys(EXPRESSIONS).filter(
      (exp) =>
        exp !== state.current.currentExpression &&
        exp !== "blink" &&
        exp !== "lookAround"
    );
    return expressions[Math.floor(Math.random() * expressions.length)];
  };

  useFrame(({ clock }) => {
    if (!morphMeshesRef.current.length) return;

    const time = clock.getElapsedTime();
    const { transitionDuration, currentValues, targetValues } = state.current;

    // Update facial expression transition
    state.current.transitionProgress += 0.02 / transitionDuration;

    if (state.current.transitionProgress >= 1) {
      state.current.transitionProgress = 0;
      state.current.currentExpression = state.current.nextExpression;
      state.current.nextExpression = pickNextExpression();
      updateTargetValues(state.current.nextExpression);
    }

    // Apply current expression transition
    morphTargetNames.forEach((targetName) => {
      const current = currentValues[targetName] || 0;
      const target = targetValues[targetName] || 0;
      setMorphTargetValue(
        targetName,
        current,
        target,
        state.current.transitionProgress
      );
    });

    // Handle blinking
    if (time - state.current.lastBlinkTime > 3 + Math.random() * 2) {
      state.current.isBlinking = true;
      state.current.blinkProgress = 0;
      state.current.lastBlinkTime = time;
    }

    if (state.current.isBlinking) {
      state.current.blinkProgress += 0.1;
      const blinkValue = Math.sin(state.current.blinkProgress) * 0.5 + 0.5;

      if (state.current.blinkProgress >= Math.PI) {
        state.current.isBlinking = false;
      } else {
        setMorphTargetValue("eyeBlinkLeft", 0, 1, blinkValue);
        setMorphTargetValue("eyeBlinkRight", 0, 1, blinkValue);
      }
    }
  });

  return (
    <group
      ref={group}
      dispose={null}
      position={position}
      scale={typeof scale === "number" ? [scale, scale, scale] : scale}
      rotation={rotation}
    >
      <primitive object={nodes.Scene} />
    </group>
  );
}

// Preload model
useGLTF.preload("/models/sentiment.glb");
